/**
 * Touch on the canvas (integration plan slice 4): a pointer's down and up
 * decoded into a tap or a swipe, and what each asks the controller for. A tap
 * (down and up within `uiTiming.tapMaxMs` and `uiTiming.tapMaxPx`) picks the
 * explored tile under it through the view's `pickTile` and starts a route
 * there (while a route runs, the new route replaces it once the step in
 * progress has played; a tap in the fog only stops it). A swipe (a drag of at
 * least `uiTiming.swipeMinPx`) turns the leader toward its 8-way direction,
 * screen up being north (the camera always looks north), and stops a route,
 * the turn following its step in progress. A drag between the two is
 * ignored. Only the primary pointer and, for a mouse, only the primary button
 * count; the pad and the HUD sit over the canvas and take their own pointers.
 * One decoder for every view (slice 6a): `bindGestures` takes the handlers,
 * the dungeon's (`dungeonGestures`) or a walkable scene's (a tap routes over
 * the ground, a swipe does nothing).
 */
import type { Dir } from '@ab/game-core';
import { uiTiming } from '@ab/ui';
import type { Controller } from './controller.ts';

export type Gesture = { readonly kind: 'tap'; readonly x: number; readonly y: number } | { readonly kind: 'swipe'; readonly dir: Dir } | null;

type Point = { readonly x: number; readonly y: number; readonly t: number };

/** Screen directions counter-clockwise from east, one per 45° sector (screen y grows downward). */
const sectors: readonly Dir[] = ['e', 'ne', 'n', 'nw', 'w', 'sw', 's', 'se'];

/** A pointer's down and up as a gesture: a tap where it went down, a swipe toward the drag's 8-way direction, or nothing. */
export const decodeGesture = (down: Point, up: Point): Gesture => {
  const dx = up.x - down.x;
  const dy = up.y - down.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= uiTiming.tapMaxPx && up.t - down.t <= uiTiming.tapMaxMs) return { kind: 'tap', x: down.x, y: down.y };
  if (distance < uiTiming.swipeMinPx) return null;
  const sector = Math.round(Math.atan2(-dy, dx) / (Math.PI / 4));
  return { kind: 'swipe', dir: sectors[(sector + sectors.length) % sectors.length] ?? 'n' };
};

/** What a gesture asks for: the dungeon's routes and turns, or a scene's routes (slice 6a; a swipe does nothing there). */
export type GestureHandlers = { readonly tap: (x: number, y: number) => void; readonly swipe: (dir: Dir) => void };

/** The dungeon's handlers: a tap routes to the explored tile under it, a swipe turns the leader. */
export const dungeonGestures = (controller: Controller): GestureHandlers => ({
  tap(x, y) {
    // A second tap routes to its tile instead (after the step in progress); one in the fog only stops the route.
    const tile = controller.view.pickTile(x, y);
    if (tile !== null) void controller.routeTo(tile);
    else controller.cancelRoute();
  },
  swipe(dir) {
    controller.input({ type: 'turn', dir });
  },
});

/** Listens for taps and swipes on the canvas (the dungeon's controller); returns the unsubscribe. */
export const bindTouch = (canvas: HTMLCanvasElement, controller: Controller): (() => void) => bindGestures(canvas, dungeonGestures(controller));

/** Listens for taps and swipes on the canvas and hands each to `handlers`; returns the unsubscribe. */
export const bindGestures = (canvas: HTMLCanvasElement, handlers: GestureHandlers): (() => void) => {
  let down: (Point & { readonly id: number }) | null = null;
  const local = (e: PointerEvent): Point => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top, t: e.timeStamp };
  };
  const onDown = (e: PointerEvent): void => {
    if (!e.isPrimary || e.button !== 0) return;
    down = { ...local(e), id: e.pointerId };
  };
  const onUp = (e: PointerEvent): void => {
    if (down === null || e.pointerId !== down.id) return;
    const gesture = decodeGesture(down, local(e));
    down = null;
    if (gesture === null) return;
    if (gesture.kind === 'swipe') handlers.swipe(gesture.dir);
    else handlers.tap(gesture.x, gesture.y);
  };
  const onCancel = (e: PointerEvent): void => {
    if (down?.id === e.pointerId) down = null;
  };
  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onCancel);
  return () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onCancel);
  };
};
